import { useEffect, useRef, useState } from "react";
import { MapPin, X } from "@phosphor-icons/react";

const TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "";
const ENDPOINT = "https://api.mapbox.com/search/geocode/v6/forward";

/* Address autocomplete (Mapbox Geocoding v6).
 *
 * Props:
 *  - value         : controlled string (the raw input text)
 *  - onChange(str) : called on every keystroke (so parent stores typed text)
 *  - onPick(parts) : called when user selects a suggestion. `parts` is
 *                    { address, city, state, zip, country, full, lng, lat }
 *  - placeholder, testId
 *  - countries     : optional comma-separated ISO codes (default "us")
 *
 * If REACT_APP_MAPBOX_TOKEN is not set, the component degrades gracefully
 * to a plain text input — no suggestions, no errors. */
export default function AddressAutocomplete({
    value,
    onChange,
    onPick,
    placeholder = "Start typing an address…",
    testId = "address-autocomplete",
    countries = "us",
}) {
    const [open, setOpen] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [highlighted, setHighlighted] = useState(0);
    const inputRef = useRef(null);
    const boxRef = useRef(null);
    const abortRef = useRef(null);
    const debounceRef = useRef(null);

    /* Close suggestions when clicking outside */
    useEffect(() => {
        function handler(e) {
            if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
        }
        window.addEventListener("mousedown", handler);
        return () => window.removeEventListener("mousedown", handler);
    }, []);

    function fetchSuggestions(q) {
        if (!TOKEN || !q || q.trim().length < 3) {
            setSuggestions([]);
            return;
        }
        if (abortRef.current) abortRef.current.abort();
        const ctrl = new AbortController();
        abortRef.current = ctrl;
        setLoading(true);
        const url = `${ENDPOINT}?q=${encodeURIComponent(q)}&autocomplete=true&country=${countries}&limit=6&access_token=${TOKEN}`;
        fetch(url, { signal: ctrl.signal })
            .then((r) => r.json())
            .then((d) => {
                const feats = (d.features || []).map(parseFeature).filter(Boolean);
                setSuggestions(feats);
                setHighlighted(0);
                setOpen(feats.length > 0);
            })
            .catch(() => { /* ignore aborts / network errors */ })
            .finally(() => setLoading(false));
    }

    function onInputChange(e) {
        const v = e.target.value;
        onChange(v);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => fetchSuggestions(v), 220);
    }

    function pick(s) {
        onChange(s.address || s.full);
        onPick && onPick(s);
        setOpen(false);
        setSuggestions([]);
        inputRef.current?.blur();
    }

    function onKeyDown(e) {
        if (!open || suggestions.length === 0) return;
        if (e.key === "ArrowDown") { e.preventDefault(); setHighlighted((i) => Math.min(i + 1, suggestions.length - 1)); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setHighlighted((i) => Math.max(i - 1, 0)); }
        else if (e.key === "Enter") { e.preventDefault(); pick(suggestions[highlighted]); }
        else if (e.key === "Escape") { setOpen(false); }
    }

    function clear() {
        onChange("");
        setSuggestions([]);
        setOpen(false);
        inputRef.current?.focus();
    }

    return (
        <div ref={boxRef} className="relative">
            <div className="relative">
                <input
                    ref={inputRef}
                    type="text"
                    className="input pr-8"
                    value={value || ""}
                    placeholder={placeholder}
                    onChange={onInputChange}
                    onFocus={() => suggestions.length > 0 && setOpen(true)}
                    onKeyDown={onKeyDown}
                    autoComplete="off"
                    data-testid={testId}
                />
                {value && (
                    <button
                        type="button"
                        onClick={clear}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-[var(--rom-text-3)] hover:text-[var(--rom-text)]"
                        data-testid={`${testId}-clear`}
                        title="Clear"
                    >
                        <X size={12} weight="bold" />
                    </button>
                )}
            </div>

            {!TOKEN && (
                <div className="text-[11px] text-[var(--rom-text-3)] mt-1">
                    Address autocomplete is off (no Mapbox token configured).
                </div>
            )}

            {open && (
                <div className="absolute left-0 right-0 top-full mt-1 z-30 bg-white rounded-[4px] border border-[var(--rom-border-strong)] shadow-lg max-h-80 overflow-y-auto" data-testid={`${testId}-dropdown`}>
                    {loading && (
                        <div className="px-3 py-2 text-xs text-[var(--rom-text-3)]">Searching…</div>
                    )}
                    {suggestions.map((s, i) => (
                        <button
                            key={s.id || s.full}
                            type="button"
                            onClick={() => pick(s)}
                            onMouseEnter={() => setHighlighted(i)}
                            data-testid={`${testId}-option-${i}`}
                            className={`w-full text-left px-3 py-2.5 flex items-start gap-2 border-b border-[var(--rom-border)] last:border-b-0 transition-colors ${
                                i === highlighted ? "bg-[var(--rom-orange-soft)]/40" : "hover:bg-slate-50"
                            }`}
                        >
                            <MapPin size={12} weight="bold" className="text-[var(--rom-orange)] mt-0.5 shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium truncate">{s.address || s.full}</div>
                                <div className="text-[11px] text-[var(--rom-text-2)] truncate">
                                    {[s.city, s.state, s.zip].filter(Boolean).join(", ")}
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

/* Map a Mapbox v6 feature → flat shape we care about. */
function parseFeature(f) {
    if (!f || !f.properties) return null;
    const p = f.properties;
    const ctx = p.context || {};
    const coords = (f.geometry && f.geometry.coordinates) || [];
    return {
        id: p.mapbox_id || p.full_address,
        full: p.full_address || p.name || "",
        address: p.name || p.full_address || "",
        city: ctx.place?.name || ctx.locality?.name || "",
        state: ctx.region?.region_code || ctx.region?.name || "",
        zip: ctx.postcode?.name || "",
        country: ctx.country?.country_code || ctx.country?.name || "",
        lng: coords[0],
        lat: coords[1],
    };
}
